import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { capitalize, differenceWith, isEmpty, isNil, sortBy } from 'lodash';
import moment from 'moment';
import arrayMove from 'array-move';

import ActionButton from 'components/buttons/action-button/ActionButton';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import { Form, FormField } from 'components/form';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Tooltip from 'components/Tooltip';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import RealmOutfitVideo from '../realm-outfit-video/RealmOutfitVideo';
import RealmOutfitsImages from '../realm-outfits-images/RealmOutfitsImages';

import { danger } from 'redux-store/reducers/common-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchRealmOutfitsTags } from 'modules/realm-outfits/reducers/realm-outfits-tags';
import {
  fetchRealmOutfitsRegions,
  resetRealmOutfitsRegions
} from 'modules/realm-outfits/reducers/realm-outfits-regions';
import {
  downloadFileIndividually
} from 'modules/realm-outfits/reducers/realm-outfits';

import { isDefined } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';
import { isFalsy } from 'utils/validators';
import {
  REALM_GENDER_OPTIONS,
  getMaxAllowedDateForOutfit,
  hasItemWithSameSKU,
  splitTagsByType
} from 'utils/realm-outfits';
import { isBlobUrl } from 'utils/url';

import './realm-outfit-details-edit.scss';

applyTranslations(require('./realm-outfit-details-edit.i18n.json'));

import type {
  Campaign,
  MixedSelectDropdownItem,
  RealmOutfit,
  RealmOutfitForm,
  RealmOutfitItemResource,
  RealmOutfitTag
} from 'types';
import {
  RealmOutfitAudioType,
  RealmOutfitImage
} from 'modules/realm-outfits/types/RealmOutfit.type';
import OutfitItems from '../outfit-items/OutfitItems';

const PUBLISHED_DATES_FORMAT = 'YYYY-MM-DD';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

type Image = Omit<RealmOutfitImage, 'sortOrder'>

type OwnProps = {
  campaign?: Campaign,
  outfit: RealmOutfit,
  isBrand?: boolean,
  onCancel: () => void,
  onSave: (a: RealmOutfitForm) => void
};

type StateProps = {
  realmOutfitsTags: Array<RealmOutfitTag>,
  availableRegions: Array<string>,
  isLoadingRegions: boolean,
  ccmOutfitsMaxItems: number,
  realmOutfitAudioTypeEnabled: boolean
};

type DispatchProps = {
  dispatchDanger: (a: string) => void,
  dispatchFetchRealmOutfitsTags: () => void,
  dispatchFetchRealmOutfitsRegions: () => void,
  dispatchResetRealmOutfitsRegions: () => void,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchDownloadFileIndividually: (url: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  canSortOutfitImages: boolean,
  outfit: RealmOutfitForm,
  isModified: boolean
};

const AUDIO_OPTIONS = Object.keys(RealmOutfitAudioType);

class RealmOutfitDetailsEdit extends React.Component<Props, State> {
  tagsFormRef: React.RefObject<any> = React.createRef();

  constructor(props: Props) {
    super(props);

    const today = moment();

    const publishedFrom = props.campaign?.campaignType === 'ccm'
      ? props.campaign?.timeline?.taskContentGoLiveDate
      : props.outfit.publishedFrom;

    const publishedUntil = props.campaign?.campaignType === 'ccm'
      ? moment(props.campaign?.timeline?.taskContentGoLiveDate).add(2, 'y').format(PUBLISHED_DATES_FORMAT)
      : props.outfit.publishedUntil;

    this.state = {
      outfit: {
        ...props.outfit,
        images: sortBy(props.outfit.images, 'sortOrder'),
        addeditems: [],
        deletedItemsIds: [],
        tags: props.outfit.tags.map(tag => tag.id),
        publishedFrom: publishedFrom || today.format(PUBLISHED_DATES_FORMAT),
        publishedUntil: publishedUntil || today.add(2, 'y').subtract(1, 'd').format(PUBLISHED_DATES_FORMAT),
        initialAudioType: props.outfit.video?.audioType ?? null
      },
      canSortOutfitImages: false,
      isModified: false
    };
  }

  componentDidMount() {
    const { dispatchFetchRealmOutfitsTags, dispatchFetchRealmOutfitsRegions } = this.props;
    dispatchFetchRealmOutfitsTags();
    dispatchFetchRealmOutfitsRegions();
  }

  componentWillUnmount() {
    const { dispatchResetRealmOutfitsRegions } = this.props;
    dispatchResetRealmOutfitsRegions();

    if (this.state.outfit.video && isBlobUrl(this.state.outfit.video.url)) {
      URL.revokeObjectURL(this.state.outfit.video.url);
    }
  }

  private getOptions(selectedOption: string | null) {
    return AUDIO_OPTIONS.map((type) => ({
      label: __t(`realm_outfit_details_edit.audio_types.${type}`),
      value: type,
      identifier: type,
      isDisabled: false,
      isFocused: false,
      isSelected: selectedOption === type
    }));
  }

  private handleAudioTypeChange = (newType: string | null): void => {
    this.setState(state => {
      const video = state.outfit.video ?? null;
      return {
        isModified: true,
        outfit: {
          ...state.outfit,
          ...(
            video ? {
              video: {
                ...video,
                audioType: newType as RealmOutfitAudioType
              }
            } : {}
          )
        }
      };
    });
  };

  private handleOutfitTagsChanged = (items: Array<MixedSelectDropdownItem>): void => {
    this.setState(state => {
      const tags = state.outfit.tags;
      const difference = differenceWith(tags, items.map(item => item.value));

      return ({
        isModified: true,
        outfit: {
          ...state.outfit,
          tags: [
            ...difference,
            ...items.filter(item => item.isSelected).map(item => item.value)
          ]
        }
      });
    });
  };

  private handleInfluencerGenderChanged = (updatedItems: Array<MixedSelectDropdownItem>) => {
    const { realmOutfitsTags } = this.props;
    const { outfit } = this.state;
    const gtlTags = splitTagsByType(realmOutfitsTags).gtl;
    const outfitGender = outfit.genders.includes('men')
      ? 'men'
      : 'women';
    const outfitTags = outfit.tags.filter(t => {
      const match = gtlTags.find(tag => tag.id === t);
      const genders = match?.genders ?? [];

      if (!match) {
        return true;
      }

      return genders.length === 0 || genders.includes(outfitGender);
    });

    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        genders: updatedItems.filter(item => item.isSelected).map(item => item.identifier),
        tags: outfitTags
      }
    }));
  };

  private handleAddItem = (item: RealmOutfitItemResource) => {
    const { dispatchDanger } = this.props;
    const { outfit } = this.state;

    if (hasItemWithSameSKU(outfit.items, item.sku)) {
      dispatchDanger(__t('realm_outfit_details_edit.items_with_same_sku', { sku: item.sku }));
      return;
    }

    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        addeditems: [
          ...state.outfit.addeditems,
          item
        ],
        items: [
          item,
          ...state.outfit.items
        ]
      }
    }));
  };

  private handleDeleteItem = (deletedItemSKU: string, deletedItemId?: number | null) => {
    const {
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t('realm_outfit_details_edit.item_delete_confirmation.message'),
      confirmButtonLabel: __t('realm_outfit_details_edit.item_delete_confirmation.button_confirm'),
      onConfirm: () => {
        this.setState(state => ({
          isModified: true,
          outfit: ({
            ...state.outfit,
            addeditems: state.outfit.addeditems.filter(item => item.sku !== deletedItemSKU),
            deletedItemsIds: [...state.outfit.deletedItemsIds, deletedItemId].filter(isDefined),
            items: state.outfit.items.filter(item => item.sku !== deletedItemSKU)
          } as RealmOutfitForm)
        }), () => dispatchHideConfirmationModal());
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  private handleOutfitRegionsChanged = (items: Array<MixedSelectDropdownItem>): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        publishedCountries: items.filter(item => item.isSelected).map(item => item.value)
      }
    }));
  };

  private handleCancel = () => {
    const { onCancel } = this.props;
    onCancel();
  };

  private handleOutfitPublishTimeframeChanged = (startDate?: string | null, endDate?: string | null): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        publishedFrom: startDate,
        publishedUntil: endDate
      }
    }));
  };

  private handleAddOutfitVideo = (file: File): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        video: {
          file,
          outfitId: state.outfit.id,
          url: URL.createObjectURL(file),
          audioType: null
        }
      }
    }));
  };

  private handleDeleteOutfitVideo = (): void => {
    const blobUrl = this.state.outfit.video?.url;

    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        audioType: null,
        video: null
      }
    }), () => {
      if (blobUrl && isBlobUrl(blobUrl)) {
        URL.revokeObjectURL(blobUrl);
      }
    });
  };

  private handleOutfitImageUploaded = (images: Array<Image>): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        images: [
          ...state.outfit.images,
          ...images
        ]
      }
    }));
  };

  private handleOutfitImageReplaced = (image: Image, position: number): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        images: [
          ...state.outfit.images.slice(0, position),
          image,
          ...state.outfit.images.slice(position + 1)
        ]
      }
    }));
  };

  private handleOutfitImageReordered = (oldIndex: number, newIndex: number): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        images: arrayMove(state.outfit.images, oldIndex, newIndex)
      }
    }));
  };

  private handleOutfitImageDeleted = (index: number): void => this.setState(state => ({
    isModified: true,
    outfit: {
      ...state.outfit,
      images: [
        ...state.outfit.images.slice(0, index),
        ...state.outfit.images.slice(index + 1, state.outfit.images.length)
      ]
    }
  }));

  private handleToggleSortingForOutfitImages = (canSortOutfitImages: boolean): void => this.setState(() => ({
    canSortOutfitImages
  }));

  private handleSave = (event) => {
    const { campaign, onSave, dispatchDanger } = this.props;
    const { outfit } = this.state;

    const isValid = this.tagsFormRef.current?.handleSubmit(event);

    const publishedTimeframeLength = moment(outfit.publishedUntil)
      .diff(moment(outfit.publishedFrom), 'years', true);

    const incorrectPublishedTimeframe = campaign?.campaignType === 'ccm'
      ? publishedTimeframeLength > 2
      : publishedTimeframeLength >= 2;

    if (incorrectPublishedTimeframe) {
      dispatchDanger(__t('realm_outfit_details_edit.errors.publish_date'));
      return;
    }

    if (isValid) {
      onSave(this.state.outfit);
    }
  };

  private handleDeleteAllOutfitImages = (): void => this.setState(state => ({
    isModified: true,
    outfit: {
      ...state.outfit,
      imageUrls: []
    }
  }));

  private getValidationError = () => {
    const { campaign, ccmOutfitsMaxItems } = this.props;
    const { outfit } = this.state;
    const isCcm = campaign?.campaignType === 'ccm';
    const focusSkus = outfit.items.filter(item => item.isFocusSku);

    if (outfit.images.length === 0) {
      return 'min_images_error';
    }

    if (isCcm && outfit.items.length > ccmOutfitsMaxItems) {
      return 'max_items_error';
    }

    if (isCcm && isEmpty(focusSkus)) {
      return 'no_focus_sku_error';
    }

    return null;
  };

  private getOptionsFromTags = (tags: Array<RealmOutfitTag>, selectedTags: Array<string>): Array<MixedSelectDropdownItem> => (
    tags.map(tag => ({
      label: capitalize(tag.title),
      value: tag.id,
      identifier: tag.id,
      isDisabled: false,
      isFocused: false,
      isSelected: selectedTags.includes(tag.id)
    }))
  );

  private getRegions(): Array<MixedSelectDropdownItem> {
    const { campaign, availableRegions } = this.props;
    const { outfit } = this.state;
    const isCcm = campaign?.campaignType === 'ccm';
    const regions = isCcm
      ? campaign?.countries ?? []
      : availableRegions;

    return regions.map(region => ({
      label: region.toUpperCase(),
      value: region,
      identifier: region,
      isDisabled: false,
      isFocused: false,
      isSelected: isCcm || outfit.publishedCountries?.includes(region)
    }));
  }

  private renderContextMenu = () => {
    const items = [
      {
        icon: 'trash-custom-fg',
        text: __t('realm_outfit_details_edit.context_menu.delete_all'),
        onClick: () => this.handleDeleteAllOutfitImages()
      }
    ];

    return (
      <MoreButtonWithMenu
        width={20}
        contextMenuProps={{ items }}
      />
    );
  };

  private renderItems() {
    const { items } = this.state.outfit;

    return (
      <OutfitItems
        editMode="edit"
        title={__t('realm_outfit_details_edit.add_items_title')}
        onAddNew={this.handleAddItem}
        items={items}
        onDeleteItem={(sku, id) => this.handleDeleteItem(sku, id)}
      />
    );
  }

  private renderDetailsForm() {
    const { realmOutfitsTags, realmOutfitAudioTypeEnabled } = this.props;
    const { outfit } = this.state;
    const outfitTags = splitTagsByType(realmOutfitsTags);
    const outfitGender = outfit.genders.includes('men')
      ? 'men'
      : 'women';
    const audioType = outfit.video?.audioType ?? null;
    const gtlFilteredByGender = outfitTags.gtl.filter(
      t => isNil(t.genders) || (t.genders ?? []).length === 0
        || (t.genders ?? []).includes(outfitGender)
    );
    const gtlTags = this.getOptionsFromTags(gtlFilteredByGender, outfit.tags);

    return (
      <Form
        ref={this.tagsFormRef}
        className="realm-outfit-details-edit__details"
      >
        { realmOutfitAudioTypeEnabled && !isNil(outfit.video) &&
        <FormField
          showValidationIcon={false}
          label={__t('realm_outfit_details_edit.label_audio_type')}
          labelType="highlighted"
          direction="column"
          fontSize="small"
        >
          <MixedSelectDropdown
            withRemovable
            className="realm-outfit-details-edit__compact-select"
            name="audio-type"
            withMulti={false}
            items={this.getOptions(audioType)}
            onItemsToggle={(_, __, selectedItem) => {
              if (typeof selectedItem?.value === 'string') {
                this.handleAudioTypeChange(selectedItem.value);
              }
            }}
            onItemsRemoveAll={() => {
              this.handleAudioTypeChange(null);
            }}
          />
        </FormField>
        }
        <FormField
          showValidationIcon={false}
          label={__t('realm_outfit_details_edit.label_tags')}
          labelType="highlighted"
          direction="column"
          fontSize="small"
        >
          <MixedSelectDropdown
            key={`realm-outfit-details-edit__main-tags-${JSON.stringify(outfitTags.regular)}`}
            className="realm-outfit-details-edit__compact-select"
            name="tags"
            placeholder={__t('realm_outfit_details_edit.label_tags_placeholder')}
            withMulti
            withRemovable
            items={this.getOptionsFromTags(outfitTags.regular, outfit.tags)}
            onItemsToggle={this.handleOutfitTagsChanged}
            onItemsToggleAll={this.handleOutfitTagsChanged}
            onItemsRemove={this.handleOutfitTagsChanged}
            onItemsRemoveAll={this.handleOutfitTagsChanged}
          />
        </FormField>
        <FormField
          label={__t('realm_outfit_details_edit.label_gender')}
          labelType="highlighted"
          direction="column"
          showRequiredMark={false}
          showValidationIcon={false}
          fontSize="small"
        >
          <MixedSelectDropdown
            withRemovable
            name="gender"
            items={REALM_GENDER_OPTIONS.map(gender => ({
              identifier: gender,
              value: gender,
              isDisabled: false,
              isFocused: false,
              label: __t(`realm_outfit_details_edit.influencer_gender_options.${gender}_label`),
              isSelected: outfit.genders.includes(gender)
            }))}
            className="realm-outfit-details-edit__compact-select"
            onItemsRemoveAll={this.handleInfluencerGenderChanged}
            onItemsRemove={this.handleInfluencerGenderChanged}
            onItemsToggle={this.handleInfluencerGenderChanged}
          />
        </FormField>
        <FormField
          showValidationIcon={false}
          label={__t('realm_outfit_details_edit.label_gtl_tags')}
          labelType="highlighted"
          direction="column"
          fontSize="small"
          validators={[
            isFalsy(
              gtlTags.filter(tag => tag.isSelected).length === 0,
              __t('realm_outfit_details_edit.errors.no_gtl_tags')
            )
          ]}
        >
          <MixedSelectDropdown
            key={`realm-outfit-details-edit__main-tags-${JSON.stringify(outfitTags.gtl)}-${JSON.stringify(outfit.genders)}`}
            name="tags"
            placeholder={__t('realm_outfit_details_edit.label_gtl_tags_placeholder')}
            withRemovable
            isDisabled={outfit.genders.length === 0}
            className="realm-outfit-details-edit__compact-select"
            items={gtlTags}
            onItemsToggle={this.handleOutfitTagsChanged}
            onItemsToggleAll={this.handleOutfitTagsChanged}
            onItemsRemove={this.handleOutfitTagsChanged}
            onItemsRemoveAll={this.handleOutfitTagsChanged}
          />
        </FormField>
      </Form>
    );
  }

  private renderPrimaryButton(): React.ReactNode {
    const { isModified } = this.state;
    const validationError = this.getValidationError();

    return (
      <ActionButton
        color="primary"
        disabled={!isModified || Boolean(validationError)}
        onClick={this.handleSave}
      >
        {__t('realm_outfit_details_edit.action_save')}
      </ActionButton>
    );
  }

  private renderButtons() {
    const { isBrand } = this.props;
    const { isModified } = this.state;
    const validationError = this.getValidationError();
    let saveButton = this.renderPrimaryButton();
    let tooltipMsgKey = '';

    if (!isModified) {
      tooltipMsgKey = 'realm_outfit_details_edit.no_changes_made';
    }

    if (validationError) {
      tooltipMsgKey = `realm_outfit_details_edit.${validationError}`;
    }

    if (!isModified || validationError) {
      saveButton = (
        <Tooltip
          message={__t(tooltipMsgKey)}
          classNameWrapper="realm-outfit-details-edit__tooltip"
        >
          {saveButton}
        </Tooltip>
      );
    }

    const buttonClasses = classnames('realm-outfit-details-edit__buttons', {
      'realm-outfit-details-edit__buttons--brand': isBrand
    });

    return (
      <div className={buttonClasses}>
        <ActionButton
          color="tertiary"
          onClick={this.handleCancel}
        >
          {__t('realm_outfit_details_edit.action_cancel')}
        </ActionButton>
        {saveButton}
      </div>
    );
  }

  private renderDatesInput() {
    const { campaign } = this.props;
    const { outfit } = this.state;
    const isDisabled = campaign?.campaignType === 'ccm';

    return (
      <div className="realm-outfit-details-edit__published-dates">
        <FormField
          direction="column"
          showRequiredMark={false}
          showValidationIcon={false}
          label={__t('realm_outfit_details_edit.label_published_timeframe')}
          labelType="highlighted"
          fontSize="small"
        >
          <DatePickerMulti
            isDisabled={isDisabled}
            startDateName={`startDate-${outfit.id}`}
            startDate={outfit.publishedFrom}
            endDateName={`endDate-${outfit.id}`}
            endDate={outfit.publishedUntil}
            noPastDates
            maxAllowableDate={getMaxAllowedDateForOutfit().format(PUBLISHED_DATES_FORMAT)}
            onChange={this.handleOutfitPublishTimeframeChanged}
          />
        </FormField>
      </div>
    );
  }

  private renderRegions() {
    const { campaign, availableRegions, isLoadingRegions } = this.props;

    return (
      <div className="realm-outfit-details-edit__published-regions">
        <FormField
          direction="column"
          showRequiredMark={false}
          showValidationIcon={false}
          label={__t('realm_outfit_details_edit.label_published_countries')}
          labelType="highlighted"
          fontSize="small"
        >
          <MixedSelectDropdown
            key={`realm-outfit-details-edit__regions--${availableRegions.length}`}
            name="realm-outfit-details-edit__regions"
            className="realm-outfit-details-edit__publish-country-select"
            items={this.getRegions()}
            withMulti
            withRemovable
            withSortable
            withCheckboxes
            withSelectAll
            isDisabled={isLoadingRegions || campaign?.campaignType === 'ccm'}
            onItemsToggle={this.handleOutfitRegionsChanged}
            onItemsRemove={this.handleOutfitRegionsChanged}
            onItemsRemoveAll={this.handleOutfitRegionsChanged}
            onItemsToggleAll={this.handleOutfitRegionsChanged}
          />
        </FormField>
      </div>
    );
  }

  renderVideo(): JSX.Element {
    const {
      isBrand,
      dispatchDownloadFileIndividually
    } = this.props;
    const { outfit } = this.state;
    const video = outfit.video;

    return (
      <div>
        <div className="realm-outfit-details-edit__heading">
          {__t('realm_outfit_details_edit.header_video')}
        </div>
        <RealmOutfitVideo
          onAddOutfitVideo={(files) => this.handleAddOutfitVideo(files[0])}
          video={video}
          isEditMode
          isInModal={isBrand}
          onDeleteVideo={this.handleDeleteOutfitVideo}
          onDownloadVideo={dispatchDownloadFileIndividually}
        />
      </div>
    );
  }

  render() {
    const { canSortOutfitImages, outfit } = this.state;
    const { isBrand } = this.props;

    return (
      <div className="realm-outfit-details-edit">
        <div className="realm-outfit-details-edit__heading">
          {__t('realm_outfit_details_edit.title')}
          {this.renderContextMenu()}
        </div>
        <div className="realm-outfit-details-edit__images-container">
          <RealmOutfitsImages
            isInModal={isBrand}
            canUpload
            canSortImages={canSortOutfitImages}
            images={outfit.images}
            onUpload={(images, position) => {
              if (position) {
                this.handleOutfitImageReplaced(images[0], position);
              } else {
                this.handleOutfitImageUploaded(images);
              }
            }}
            onDelete={this.handleOutfitImageDeleted}
            onImageReorder={this.handleOutfitImageReordered}
            onRearrangeOrder={this.handleToggleSortingForOutfitImages}
          />
        </div>
        {this.renderVideo()}
        {this.renderDetailsForm()}
        {this.renderRegions()}
        {this.renderDatesInput()}
        {this.renderItems()}
        {this.renderButtons()}
      </div>
    );
  }
}

const mapStateToProps = ({
  realmOutfitsRegions,
  realmOutfitsTags,
  runtimeConfig
}): StateProps => ({
  realmOutfitsTags: realmOutfitsTags.tags,
  availableRegions: realmOutfitsRegions.regions,
  isLoadingRegions: realmOutfitsRegions.isLoading,
  ccmOutfitsMaxItems: runtimeConfig.config.ccmOutfitsMaxItems,
  realmOutfitAudioTypeEnabled: runtimeConfig.config.realmOutfitAudioTypeEnabled ?? false
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDanger: (message) => dispatch(danger(message)),
  dispatchFetchRealmOutfitsTags: () => dispatch(fetchRealmOutfitsTags()),
  dispatchFetchRealmOutfitsRegions: () => dispatch(fetchRealmOutfitsRegions()),
  dispatchResetRealmOutfitsRegions: () => dispatch(resetRealmOutfitsRegions()),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchDownloadFileIndividually: url => dispatch(downloadFileIndividually(url))
});

export default connect(mapStateToProps, mapDispatchToProps)(RealmOutfitDetailsEdit);
